import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { CheckCircle2, XCircle, Clock, Mail, DollarSign, Edit2, Save, X } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface QuoteRequestCardProps {
  request: any;
  leadId: string;
}

export function QuoteRequestCard({ request, leadId }: QuoteRequestCardProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [quotedPrice, setQuotedPrice] = useState(request.quoted_price?.toString() || "");
  const [quotedCurrency, setQuotedCurrency] = useState(request.quoted_currency || "ZAR");
  const [supplierNotes, setSupplierNotes] = useState(request.supplier_notes || "");
  const [status, setStatus] = useState(request.status);

  const updateQuoteMutation = useMutation({
    mutationFn: async () => {
      const updates: any = {
        status,
      };

      if (status === "quoted") {
        if (!quotedPrice) {
          throw new Error("Please enter a quoted price");
        }
        updates.quoted_price = parseFloat(quotedPrice);
        updates.quoted_currency = quotedCurrency;
        updates.supplier_notes = supplierNotes || null;
        updates.response_date = new Date().toISOString();
      }

      const { error } = await supabase
        .from("supplier_quote_requests")
        .update(updates)
        .eq("id", request.id);

      if (error) throw error;

      // If accepting a single item quote, update the itinerary item
      if (status === "quoted" && request.request_type === "single_item" && request.itinerary_item_id) {
        const { error: itemError } = await supabase
          .from("itinerary_items")
          .update({
            cost: parseFloat(quotedPrice),
            supplier: request.suppliers?.name,
          })
          .eq("id", request.itinerary_item_id);

        if (itemError) throw itemError;

        // Log activity
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase.from("activity_log").insert({
            lead_id: leadId,
            user_id: user.id,
            action: "Supplier Quote Accepted",
            details: `Accepted quote from ${request.suppliers?.name} for ${request.itinerary_items?.title}: ${quotedCurrency} ${quotedPrice}`,
          });
        }
      }
    },
    onSuccess: () => {
      toast.success("Quote updated successfully");
      queryClient.invalidateQueries({ queryKey: ["supplier-quote-requests", leadId] });
      if (request.request_type === "single_item") {
        queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      }
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; icon: any; label: string }> = {
      sent: { variant: "secondary", icon: Mail, label: "Sent" },
      viewed: { variant: "secondary", icon: Clock, label: "Viewed" },
      quoted: { variant: "default", icon: DollarSign, label: "Quoted" },
      declined: { variant: "destructive", icon: XCircle, label: "Declined" },
      expired: { variant: "outline", icon: Clock, label: "Expired" },
    };

    const config = variants[status] || variants.sent;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  return (
    <Card className="p-4">
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold">{request.suppliers?.name}</h4>
              {getStatusBadge(status)}
            </div>
            {request.suppliers?.supplier_categories && (
              <p className="text-xs text-muted-foreground">
                {request.suppliers.supplier_categories.name}
              </p>
            )}
          </div>
          {!isEditing && status === "sent" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditing(true)}
            >
              <Edit2 className="h-3 w-3 mr-1" />
              Enter Response
            </Button>
          )}
        </div>

        {/* Request Details */}
        <div className="text-sm space-y-1">
          <p className="text-muted-foreground">
            Requested: {format(new Date(request.request_date), "MMM d, yyyy")}
          </p>
          {request.expires_at && (
            <p className="text-muted-foreground">
              Deadline: {format(new Date(request.expires_at), "MMM d, yyyy")}
            </p>
          )}
          {request.notes_to_supplier && (
            <div className="mt-2 p-2 bg-muted rounded text-xs">
              <strong>Notes:</strong> {request.notes_to_supplier}
            </div>
          )}
        </div>

        {/* Edit Mode */}
        {isEditing && (
          <div className="space-y-3 pt-3 border-t">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Quoted Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={quotedPrice}
                  onChange={(e) => setQuotedPrice(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Select value={quotedCurrency} onValueChange={setQuotedCurrency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="ZAR">ZAR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Supplier Notes</Label>
              <Textarea
                placeholder="Any notes or conditions..."
                value={supplierNotes}
                onChange={(e) => setSupplierNotes(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => updateQuoteMutation.mutate()}
                disabled={updateQuoteMutation.isPending}
              >
                <Save className="h-3 w-3 mr-1" />
                Save Response
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setQuotedPrice(request.quoted_price?.toString() || "");
                  setQuotedCurrency(request.quoted_currency || "ZAR");
                  setSupplierNotes(request.supplier_notes || "");
                  setStatus(request.status);
                }}
              >
                <X className="h-3 w-3 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Display Quote Response */}
        {!isEditing && request.status === "quoted" && request.quoted_price && (
          <div className="pt-3 border-t space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Quoted Price:</span>
              <span className="text-lg font-bold text-primary">
                {request.quoted_currency} {request.quoted_price.toLocaleString()}
              </span>
            </div>
            {request.supplier_notes && (
              <div className="p-2 bg-muted rounded text-sm">
                <strong>Supplier Notes:</strong> {request.supplier_notes}
              </div>
            )}
            {request.response_date && (
              <p className="text-xs text-muted-foreground">
                Responded: {format(new Date(request.response_date), "MMM d, yyyy 'at' h:mm a")}
              </p>
            )}
          </div>
        )}

        {/* Display Declined */}
        {!isEditing && request.status === "declined" && (
          <div className="pt-3 border-t">
            <div className="flex items-center gap-2 text-destructive">
              <XCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Supplier declined this request</span>
            </div>
            {request.supplier_notes && (
              <div className="mt-2 p-2 bg-muted rounded text-sm">
                <strong>Reason:</strong> {request.supplier_notes}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
